import {
  EllipsisOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  UploadOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Dropdown,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Upload,
  Input
} from 'antd'
import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '~/components/PageHeader'
import { apiDeleteRegistration, apiRefundRegistration } from '~/api/adminApi'
import SecureFormPostActionButton from '~/components/SecureFormPostActionButton'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { type Registration } from '~/types/course'
import {
  backendUrl,
  compareNumber,
  compareText,
  countryCodeToName,
  formatDateTime,
  showErrors
} from '~/utils/helpers'
import {
  useImportStudentMutation,
  useSendAllLoginDetailMutation,
  useSendStudentLoginDetailMutation
} from './CourseParticipants/mutation'

const { confirm } = Modal

export default function RegistrationList({ course }: { course: any }) {
  const dataSource = useTableDataSourceKey<Registration>(course.registrations)
  const [filterInput, setFilterInput] = useState('')
  const { mutate: importStudent, isLoading: uploading } =
    useImportStudentMutation(course.id)
  const { mutate: sendLoginDetails, isLoading: isSendingLoginDetails } =
    useSendAllLoginDetailMutation(course.id)
  const { mutate: sendStudentLogin } = useSendStudentLoginDetailMutation(
    course.id
  )
  const queryClient = useQueryClient()
  const deleteRegistrationMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiDeleteRegistration(id)
    },
    onError: showErrors,
    onSuccess: async () => {
      message.success('Delete successfully')
      await queryClient.invalidateQueries({
        queryKey: ['courses', Number(course.id)]
      })
    }
  })
  const refundRegistrationMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRefundRegistration(id)
    },
    onError: showErrors,
    onSuccess: async () => {
      message.success('Refund successfully')
      await queryClient.invalidateQueries({
        queryKey: ['courses', Number(course.id)]
      })
    }
  })

  const confirmSendLogin = (
    item: Registration,
    testing: boolean = false
  ): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to send the login details to ${item.first_name}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        sendStudentLogin({ studentId: item.id, testing })
      }
    })
  }

  const confirmDeleteRegistration = (item: Registration): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to delete the registration with email ${item.email}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        deleteRegistrationMutation.mutate(item.id)
      }
    })
  }

  const confirmRefundRegistration = (item: Registration): void => {
    confirm({
      title: 'Confirm refund',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to refund the registration with email ${item.email}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        refundRegistrationMutation.mutate(item.id)
      }
    })
  }

  const filterData = useMemo(() => {
    const filter = filterInput.toLowerCase()
    if (!Array.isArray(dataSource)) {
      return []
    }

    return dataSource.filter(
      (student) =>
        `${student.first_name} ${student.last_name}`
          .toLowerCase()
          .includes(filter) || student.email.toLowerCase().includes(filter)
    )
  }, [dataSource, filterInput])

  return (
    <>
      <PageHeader
        className='radup-page-header'
        extra={[
          <Col key={1}>
            <Row>
              <Space>
                <Input.Search
                  placeholder='Search'
                  allowClear
                  enterButton={<SearchOutlined />}
                  onSearch={setFilterInput}
                />
                <Upload
                  customRequest={(data) => {
                    importStudent(data)
                  }}
                  showUploadList={false}
                >
                  <Button
                    type='default'
                    icon={<UploadOutlined />}
                    loading={uploading}
                  >
                    Import registrations
                  </Button>
                </Upload>
                <SecureFormPostActionButton
                  key='download'
                  actionUrl={backendUrl(
                    `statistic/courses/${course.id}/export-registrations`
                  )}
                  title='Download registrations'
                />
                <Popconfirm
                  title='Do you want to send the login details to all participants?'
                  onConfirm={() => {
                    sendLoginDetails(null)
                  }}
                  okText='YES'
                >
                  <Button
                    type='primary'
                    loading={isSendingLoginDetails}
                    icon={<MailOutlined />}
                  >
                    Send all login details
                  </Button>
                </Popconfirm>
              </Space>
            </Row>
          </Col>
        ]}
      />

      <Table
        size='small'
        tableLayout='auto'
        dataSource={filterData}
        // rowClassName={(record) => (record.cancelled_at ? 'disabled-row' : '')}
        footer={(data) => (
          <Row justify='start'>
            <span>Total: {dataSource?.length} participants</span>
          </Row>
        )}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a, b) => compareNumber(a.id, b.id)
          },
          {
            title: 'First Name',
            dataIndex: 'first_name',
            render: (firstName) => firstName,
            sorter: (a, b) => compareText(a.first_name, b.first_name)
          },
          {
            title: 'Last Name',
            dataIndex: 'last_name',
            render: (lastName) => lastName,
            sorter: (a, b) => compareText(a.last_name, b.last_name)
          },
          {
            title: 'Email',
            render: (student) => student?.email,
            sorter: (a, b) => compareText(a.email, b.email)
          },
          {
            title: 'Type',
            dataIndex: 'join_type'
          },
          {
            title: 'Country',
            render: (item) => countryCodeToName(item.country_code)
          },
          {
            title: 'Institution',
            render: (item) => item.institution
          },
          {
            title: 'Payment Status',
            render: (item) => item.payment_status
          },
          {
            title: 'Coupon',
            render: (item) => item.coupon_code
          },
          {
            title: 'Discount',
            render: (item) =>
              item.discount_amount != null
                ? `${item.currency ?? course.currency ?? 'USD'} ${
                    item.discount_amount
                  }`
                : ''
          },
          {
            title: 'Paid Amount',
            render: (item) => {
              if (item.payment_status === 'paid') {
                return item.payment_amount != null
                  ? `${item.currency ?? course.currency ?? 'USD'} ${
                      item.payment_amount
                    }`
                  : ''
              }
              return ''
            }
          },
          {
            title: 'Cancelled At',
            dataIndex: 'cancelled_at',
            render: (cancelledAt) => formatDateTime(cancelledAt)
          },
          {
            title: 'Refunded At',
            dataIndex: 'refunded_at',
            render: (refundedAt) => formatDateTime(refundedAt)
          },
          {
            title: 'Actions',
            render: (item) => {
              return (
                <Space size='middle'>
                  <Dropdown
                    key={2}
                    className='dropdown-action'
                    placement='bottom'
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          onClick: () => {
                            confirmSendLogin(item)
                          },
                          disabled:
                            item.enabled === false &&
                            item.join_type !== 'viewer',
                          label: 'Send login info',
                          key: 'Send login info'
                        },
                        {
                          onClick: () => {
                            confirmSendLogin(item, true)
                          },
                          disabled: item.enabled === false,
                          label: 'Send test login email',
                          key: 'Send test login email'
                        },
                        {
                          label: 'Refund',
                          key: 'refund',
                          danger: true,
                          disabled: item.payment_status !== 'paid',
                          onClick: () => {
                            confirmRefundRegistration(item)
                          }
                        },
                        {
                          label: 'Delete',
                          key: 'delete',
                          danger: true,
                          onClick: () => {
                            // delete registration
                            confirmDeleteRegistration(item)
                          }
                        }
                      ]
                    }}
                  >
                    <Button type='primary' icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space>
              )
            }
          }
        ]}
      />
    </>
  )
}
